import {
  ABIActionDrawer,
  ABIIconButton,
  ABIInlineLoader,
  CheckIcon,
  CloseIcon,
  DrawerContainerPosition,
  EditIcon,
  InputCompactness,
  VerticalMoreIcon
} from '@ab-inbev-labs/ux-react-components';
import { NamingCase, formatToTargetCase, resolveCjsDefault } from '@ab-inbev/sam-utils';
import Send from '@abi-labs-hexa/web-v2/core/icons/Send';
import Trash from '@abi-labs-hexa/web-v2/core/icons/Trash';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ux-react-designsystem-wrappers/Input';
import { Textarea } from './ux-react-designsystem-wrappers/Textarea';
import './Comments.scss';

const SendIcon = resolveCjsDefault(Send);
const TrashIcon = resolveCjsDefault(Trash);

const formatCommentDisplayName = (displayName: string) => {
  let name = displayName.replace(/@.*$/, '').trim();
  if (name === name.toUpperCase()) {
    name = name.toLocaleLowerCase();
  }

  return formatToTargetCase(name, NamingCase.Standard);
};
export type CommentItem = {
  id?: string | null;
  comment?: string;
  createdBy?: string | null;
  createdById?: string | null;
  createdTsp?: string | null;
  lastUpdatedTsp?: string | null;
};

type QueryWrapper = {
  data?: CommentItem[] | null;
  isLoading: boolean;
};

type MutationWrapper<TPayload> = {
  isPending: boolean;
  mutate: (payload: TPayload, callbacks: MutationCallbacks) => void;
};

type MutationCallbacks = {
  onSuccess: () => void;
  onError: () => void;
};

export type CommentsProps = {
  currentUser?: { id?: string; email?: string; name?: string };
  getComments: () => QueryWrapper;
  invalidateComments: () => void;
  postComment: () => MutationWrapper<{ comment: string }>;
  putComment: () => MutationWrapper<{ id: string; comment: string }>;
  deleteComment: () => MutationWrapper<{ id: string; comment?: string }>;
  testIdPrefix?: string;
};

export const Comments = ({
  currentUser,
  getComments,
  invalidateComments,
  postComment,
  putComment,
  deleteComment,
  testIdPrefix = 'comment'
}: CommentsProps) => {
  const i18n = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const commentsQuery = getComments();
  const postMutation = postComment();
  const putMutation = putComment();
  const deleteMutation = deleteComment();
  const comments = [...(commentsQuery.data ?? [])].sort(
    (first, second) => dayjs(first.createdTsp).valueOf() - dayjs(second.createdTsp).valueOf()
  );
  const testId = (element: string, index?: number) =>
    `${testIdPrefix}-${element}${index === undefined ? '' : `-${index}`}`;

  const submitComment = () => {
    if (!newComment.trim()) return;

    setError(null);
    postMutation.mutate(
      { comment: newComment.trim() },
      {
        onSuccess: () => {
          setNewComment('');
          invalidateComments();
        },
        onError: () => setError(i18n.t('comments.submitError'))
      }
    );
  };

  const saveComment = (comment: CommentItem) => {
    if (!comment.id || !editingText.trim() || editingText.trim() === comment.comment) return;

    setError(null);
    putMutation.mutate(
      { id: comment.id, comment: editingText.trim() },
      {
        onSuccess: () => {
          setEditingCommentId(null);
          invalidateComments();
        },
        onError: () => setError(i18n.t('comments.editError'))
      }
    );
  };

  const removeComment = (comment: CommentItem) => {
    if (!comment.id) return;

    setError(null);
    deleteMutation.mutate(
      { id: comment.id, comment: comment.comment ?? '' },
      {
        onSuccess: invalidateComments,
        onError: () => setError(i18n.t('comments.deleteError'))
      }
    );
  };

  return (
    <section className="comments" data-testid={testId('section')}>
      <div className="comments-list">
        {commentsQuery.isLoading && <ABIInlineLoader />}
        {comments.map((comment, index) => {
          const isOwner =
            (!!comment.createdById &&
              !!(comment.createdById === currentUser?.id || comment.createdById === currentUser?.email)) ||
            !!(comment.createdBy && comment.createdBy === currentUser?.email);
          const isEditing = editingCommentId === comment.id;
          const isEdited =
            !!comment.createdTsp &&
            !!comment.lastUpdatedTsp &&
            dayjs(comment.lastUpdatedTsp).unix() > dayjs(comment.createdTsp).unix();
          return (
            <div className="comment-card" key={comment.id ?? index} data-testid={`${testIdPrefix}-${index}`}>
              <div className="comment-header">
                <div className="comment-author">
                  {formatCommentDisplayName(
                    comment.createdBy === currentUser?.email
                      ? (currentUser?.name ?? '')
                      : comment.createdBy || i18n.t('comments.unknownUser')
                  )}
                </div>
                {comment.createdTsp && (
                  <time dateTime={comment.createdTsp} className="comment-date">
                    {dayjs(comment.createdTsp).format('MMMM D, YYYY h:mm A')}
                  </time>
                )}
                {isEdited && <span className="comment-edited">({i18n.t('comments.edited')})</span>}
                {isOwner && comment.id && (
                  <div className="comment-actions" data-testid={testId('card-action-drawer', index)}>
                    <ABIActionDrawer
                      id={`comment-actions-${comment.id}`}
                      buttonContent={<VerticalMoreIcon width={24} />}
                      closeContainerOnOutsideClick={true}
                      drawerContainerPosition={
                        index === 0 ? DrawerContainerPosition.BottomRight : DrawerContainerPosition.TopRight
                      }
                      data-testid={testId('action-drawer', index)}
                    >
                      <ABIIconButton
                        className="comment-action"
                        imageAltName={i18n.t('common.actions.edit')}
                        label={i18n.t('common.actions.edit')}
                        labelRight={true}
                        data-testid={testId('edit', index)}
                        onClick={() => {
                          setEditingCommentId(comment.id ?? null);
                          setEditingText(comment.comment ?? '');
                        }}
                      >
                        <EditIcon width="24px" />
                      </ABIIconButton>
                      <ABIIconButton
                        className="comment-action"
                        imageAltName={i18n.t('common.actions.delete')}
                        label={i18n.t('common.actions.delete')}
                        labelRight={true}
                        data-testid={testId('delete', index)}
                        onClick={() => void removeComment(comment)}
                      >
                        <TrashIcon />
                      </ABIIconButton>
                    </ABIActionDrawer>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="comment-edit">
                  <Textarea
                    value={editingText}
                    onChange={(event) => setEditingText(event.target.value)}
                    data-testid={testId('edit-field', index)}
                  />
                  <ABIIconButton
                    imageAltName={i18n.t('common.actions.cancel')}
                    data-testid={testId('cancel', index)}
                    onClick={() => setEditingCommentId(null)}
                  >
                    <CloseIcon width="24px" />
                  </ABIIconButton>
                  <ABIIconButton
                    imageAltName={i18n.t('common.actions.ok')}
                    data-testid={testId('okay', index)}
                    disabled={!editingText.trim() || putMutation.isPending}
                    onClick={() => void saveComment(comment)}
                  >
                    <CheckIcon width="24px" />
                  </ABIIconButton>
                </div>
              ) : (
                <div className="comment-text" data-testid={`comment-${index}-text`}>
                  {comment.comment}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && <div className="comments-error">{error}</div>}
      <form
        className="comments-submit"
        onSubmit={(event) => {
          event.preventDefault();
          void submitComment();
        }}
      >
        <Input
          placeholder={i18n.t('comments.placeholder')}
          compact={InputCompactness.InnerPaddingOnly}
          maxLength={1000}
          className="comments-input"
          data-testid={testId('submit-input')}
          value={newComment}
          onChange={(event) => setNewComment(event.target.value)}
        />
        <ABIIconButton
          imageAltName={i18n.t('common.actions.submit')}
          className="comments-send"
          data-testid={testId('submit-icon')}
          disabled={!newComment.trim() || postMutation.isPending}
          onClick={() => void submitComment()}
        >
          <SendIcon />
        </ABIIconButton>
      </form>
    </section>
  );
};
